import React from 'react'
import { useTranslation } from 'react-i18next'

export type DesignTheme = 'minimal' | 'gradient' | 'neumorphism' | 'glass' | 'neon'

interface FinancialData {
  count: number
  totalAmount: number
  netCommission: number
}

interface FinancialWidgetDesignSelectorProps {
  data: FinancialData | null
  title: string
  accent: string
  designTheme: DesignTheme
}

const FinancialWidgetDesignSelector: React.FC<FinancialWidgetDesignSelectorProps> = ({
  data,
  title,
  accent,
  designTheme
}) => {
  const { t } = useTranslation()
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatAmount = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M$`
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K$`
    }
    return formatCurrency(amount)
  }

  const commission = data ? formatCurrency(data.netCommission) : '$0'
  const deals = data?.count || 0
  const financed = data ? formatAmount(data.totalAmount) : '$0'

  const getAccentColors = () => {
    switch (accent) {
      case 'pipeline': return { primary: 'blue', hex: '#3B82F6' }
      case 'funded': return { primary: 'green', hex: '#10B981' }
      case 'revenue': return { primary: 'orange', hex: '#F59E0B' }
      case 'paid': return { primary: 'purple', hex: '#8B5CF6' }
      default: return { primary: 'gray', hex: '#6B7280' }
    }
  }

  const colors = getAccentColors()

  // 1. MINIMAL DESIGN - Clean white cards with subtle borders
  if (designTheme === 'minimal') {
    return (
      <div className="space-y-4">
        <div className="text-center pb-4">
          <div className="text-3xl font-light text-gray-900 dark:text-white tabular-nums mb-1">
            {commission}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {title}
          </div>
        </div>
        <div className="flex justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="text-center">
            <div className="text-lg font-medium text-gray-900 dark:text-white">{deals}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.widgets.deals')}</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-medium text-gray-900 dark:text-white">{financed}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.widgets.financed')}</div>
          </div>
        </div>
      </div>
    )
  }

  // 2. GRADIENT DESIGN - Bold gradients with white text
  if (designTheme === 'gradient') {
    return (
      <div className="space-y-2">
        <div className="text-center">
          <div className="text-2xl font-bold text-white tabular-nums mb-1">
            {commission}
          </div>
          <div className="text-xs text-white/80 uppercase tracking-wider font-semibold">
            {title}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="bg-transparent border border-white/20 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-white">{deals}</div>
            <div className="text-xs text-white/70">{t('dashboard.widgets.deals')}</div>
          </div>
          <div className="bg-transparent border border-white/20 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-white">{financed}</div>
            <div className="text-xs text-white/70">{t('dashboard.widgets.financed')}</div>
          </div>
        </div>
      </div>
    )
  }

  // 3. NEUMORPHISM DESIGN - Soft shadows and inset effects
  if (designTheme === 'neumorphism') {
    return (
      <div className="space-y-6">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl p-6 shadow-inner" style={{
          boxShadow: 'inset 8px 8px 16px #bebebe, inset -8px -8px 16px #ffffff'
        }}>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-700 dark:text-gray-300 tabular-nums">
              {commission}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {title}
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-4 text-center" style={{
            boxShadow: '4px 4px 8px #bebebe, -4px -4px 8px #ffffff'
          }}>
            <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{deals}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.widgets.deals')}</div>
          </div>
          <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-4 text-center" style={{
            boxShadow: '4px 4px 8px #bebebe, -4px -4px 8px #ffffff'
          }}>
            <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{financed}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.widgets.financed')}</div>
          </div>
        </div>
      </div>
    )
  }

  // 4. GLASS DESIGN - Glassmorphism with backdrop blur
  if (designTheme === 'glass') {
    return (
      <div className="space-y-4 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 rounded-xl backdrop-blur-xl"></div>
        <div className="relative z-10 space-y-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums mb-2">
              {commission}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wide">
              {title}
            </div>
          </div>
          <div className="flex justify-between">
            <div className="text-center bg-white/20 dark:bg-black/20 rounded-lg p-3 backdrop-blur-sm">
              <div className="text-lg font-semibold text-gray-900 dark:text-white">{deals}</div>
              <div className="text-xs text-gray-600 dark:text-gray-300">{t('dashboard.widgets.deals')}</div>
            </div>
            <div className="text-center bg-white/20 dark:bg-black/20 rounded-lg p-3 backdrop-blur-sm">
              <div className="text-lg font-semibold text-gray-900 dark:text-white">{financed}</div>
              <div className="text-xs text-gray-600 dark:text-gray-300">{t('dashboard.widgets.financed')}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 5. NEON DESIGN - Dark with neon glowing effects
  if (designTheme === 'neon') {
    return (
      <div className="space-y-4 bg-black rounded-xl p-4" style={{
        boxShadow: `0 0 20px ${colors.hex}40`
      }}>
        <div className="text-center">
          <div 
            className="text-3xl font-bold tabular-nums mb-2"
            style={{ 
              color: colors.hex,
              textShadow: `0 0 10px ${colors.hex}, 0 0 20px ${colors.hex}, 0 0 30px ${colors.hex}`
            }}
          >
            {commission}
          </div>
          <div className="text-sm text-cyan-400 uppercase tracking-widest font-mono">
            {title}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div 
            className="bg-gray-900 rounded-lg p-3 text-center border"
            style={{ borderColor: colors.hex }}
          >
            <div className="text-lg font-bold text-white">{deals}</div>
            <div className="text-xs text-cyan-300">{t('dashboard.widgets.deals')}</div>
          </div>
          <div 
            className="bg-gray-900 rounded-lg p-3 text-center border"
            style={{ borderColor: colors.hex }}
          >
            <div className="text-lg font-bold text-white">{financed}</div>
            <div className="text-xs text-cyan-300">{t('dashboard.widgets.financed')}</div>
          </div>
        </div>
      </div>
    )
  }

  // Default fallback to minimal
  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{commission}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Commission</div>
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600">
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{deals}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">{t('dashboard.widgets.deals')}</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{financed}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">{t('dashboard.widgets.financed')}</div>
        </div>
      </div>
    </div>
  )
}

export default FinancialWidgetDesignSelector